import React, { useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  MdOutlineKeyboardArrowRight,
  MdKeyboardArrowDown,
} from "react-icons/md";
import Spinner from "./PageLoader.js";
import { useTheme } from "../context/ThemeContext.js";

const ReusableTable = ({
  data = [],
  columns,
  isLoading,
  enableMasterDetails = false,
  renderDetails,
  pageIndex = 0,
  onPageChange,
  expandedRows = {},
  onRowExpandChange,
  emptyText = "No data available",
}) => {
  const { theme } = useTheme();
  const tableData = useMemo(() => data, [data]);
  const [sorting, setSorting] = React.useState([]);

  const enhancedColumns = useMemo(() => {
    let cols = [...columns];
    if (enableMasterDetails) {
      cols.unshift({
        id: "expander",
        header: () => null,
        cell: ({ row }) => (
          <button
            onClick={() => {
              row.toggleExpanded();
              onRowExpandChange?.(row.id, !row.getIsExpanded());
            }}
            style={{ cursor: "pointer" }}
          >
            {row.getIsExpanded() ? (
              <MdKeyboardArrowDown size={20} className="table__dropdown" />
            ) : (
              <MdOutlineKeyboardArrowRight
                size={20}
                className="table__dropdown"
              />
            )}
          </button>
        ),
      });
    }
    return cols;
  }, [columns, enableMasterDetails, onRowExpandChange]);

  const table = useReactTable({
    data: tableData,
    columns: enhancedColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => enableMasterDetails,
    initialState: {
      // Add this initialState
      pagination: {
        pageIndex,
        pageSize: 10,
      },
      expanded: expandedRows,
      sorting: [],
    },
    state: {
      pagination: { pageIndex, pageSize: 10 }, // Ensuring pageSize is set
      expanded: expandedRows,
      sorting,
    },
    onSortingChange: setSorting,
    onPaginationChange: (updater) => {
      const current = table.getState().pagination.pageIndex;
      const next =
        typeof updater === "function"
          ? updater({ pageIndex: current }).pageIndex
          : updater.pageIndex;

      if (current !== next) {
        onPageChange?.(next);
      }
    },
  });

  return (
    <div
      className={`table-container ${
        theme === "dark" ? "dark-mode" : "light-mode"
      }`}
    >
      <div className="table">
        <table className="custom-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{ 
                      cursor: header.column.getCanSort() ? 'pointer' : 'default',
                      userSelect: 'none' 
                    }}
                    className={header.column.getCanSort() ? 'sortable-header' : ''}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    <span className="sort-indicator">
                      {header.column.getIsSorted()
                        ? header.column.getIsSorted() === "desc"
                          ? " ↓"
                          : " ↑"
                        : header.column.getCanSort() ? " ⇅" : ""}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <React.Fragment key={row.id}>
                <tr className="table-row">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {cell.column.columnDef.cell
                        ? cell.column.columnDef.cell(cell.getContext())
                        : cell.getValue()}
                    </td>
                  ))}
                </tr>
                {enableMasterDetails &&
                  row.getIsExpanded() &&
                  renderDetails && (
                    <tr className="expanded-row">
                      <td colSpan={enhancedColumns.length}>
                        {renderDetails(row.original)}
                      </td>
                    </tr>
                  )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {table.getRowModel().rows.length === 0 && !isLoading && (
        <div className="table__empty">
          <p>{emptyText}</p>
        </div>
      )}

      {isLoading && (
        <div className="documents__loader">
          <Spinner />
        </div>
      )}
      <div className="pagination-controls">
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          Prev
        </button>
        <span>
          Page {table.getState().pagination.pageIndex + 1} of{" "}
          {table.getPageCount() || 1}
        </span>
        <button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ReusableTable;
